import React, {useEffect, useState} from "react";
import {SessionUser} from "../../../models/session-user";
import {VehiclePlate} from "../../../models/vehicle-plate";
import {useBlockUI} from "../../../shared/services/block-ui-service";
import SessionStorage from "../../../store/session";
import useVehiclePlateService from "../../../services/backoffice/hooks/useVehiclePlateService";
import {VehiclePlateFilter} from "../../../models/vehicle-plate-filter";
import {toast, ToastOptions} from "react-toastify";
import {ToasterConfig} from "../../../config/ToasterConfig";
import {Button, Form, Grid, Header, Icon, Input, Label, Pagination, Segment, Table} from "semantic-ui-react";
import moment from "moment";
import {useNavigate} from "react-router-dom";
import {ShortDto} from "../../../models/short-dto";
import useClientService from "../../../services/backoffice/hooks/useClientService";
import TooltipInfo from "../../../components/tooltip/tooltip";
import useVehicleTypeService from "../../../services/backoffice/hooks/useVehicleTypeService";
import useFleetTypeService from "../../../services/backoffice/hooks/useFleetTypeService";

const VehiclePlatesPage = () => {
    const [vehiclePlates, setVehiclePlates] = useState<VehiclePlate[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>();
    const [filter, setFilter] = useState<VehiclePlateFilter>({ Page: 1 });
    const [totalPages, setTotalPages] = useState<number>();
    const blockUI = useBlockUI();
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const [showFilters, setShowFilters] = useState(false);
    const navigate = useNavigate();
    const [clientes, setClients] = useState<ShortDto[]>([]);
    const [vehicleTypes, setVehicleTypes] = useState<ShortDto[]>([]);
    const [fleetTypes, setFleetTypes] = useState<ShortDto[]>([]);
    const { getAllClientsShortDto } = useClientService();
    const {getAllVehicleTypesShortDto} = useVehicleTypeService();
    const {getAllFleetTypesShortDto} = useFleetTypeService();

    const {
        getVehiclePlates,
        activateVehiclePlate,
        inactivateVehiclePlate,
        deleteVehiclePlate
    } = useVehiclePlateService();

    useEffect(() => {
        blockUI?.setActive(loading);
    }, [loading]);

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user);
        }
        updateSessionUser();
    }, []);

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        loadVehiclePlates();
    }, [filter]);


    useEffect(() => {
        async function loadClients() {
            try {
                const response = await getAllClientsShortDto();
                setClients(response);
            } catch (error) {
            }
        }

        async function loadVehicleTypes() {
            try {
                const response = await getAllVehicleTypesShortDto();
                setVehicleTypes(response);
            } catch (error) {
            }
        }

        async function loadFleetTypes() {
            try {
                const response = await getAllFleetTypesShortDto();
                setFleetTypes(response);
            } catch (error) {
            }
        }

        loadClients();
        loadVehicleTypes();
        loadFleetTypes();
    }, []);



    async function loadVehiclePlates() {
        try {
            setLoading(true);
            blockUI?.setActive(true);
            const response = await getVehiclePlates(filter);
            setVehiclePlates(response.itens);
            setTotalPages(response.totalPages);
        } catch (error: any) {
            setError(error.message);
            setLoading(false);
        } finally {
            setLoading(false);
            blockUI?.setActive(false);
        }
    }


    const toggleActivationPlate = async (plate: VehiclePlate) => {
        try {
            const result = window.confirm(`Tem certeza que deseja ${plate.isActive ? 'desativar' : 'ativar'} a placa ${plate.plate}?`);
            if (!result) return;
            setLoading(true);
            const resultAction = !plate.isActive ? await activateVehiclePlate(plate.id) : await inactivateVehiclePlate(plate.id);
            if (resultAction) {
                toast.success(`A placa foi ${plate.isActive ? 'desativada' : 'ativada'} com sucesso`, ToasterConfig.success);
                loadVehiclePlates();
            }
        } catch (error: any) {
            toast.warn(`Falha ao ${plate.isActive ? 'desativar' : 'ativar'} placa`, ToasterConfig.error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    async function deletePlateFunction(plate: VehiclePlate) {
        try {
            const confirm = window.confirm(`Tem certeza que deseja apagar a placa ${plate.plate}?`);
            if (confirm) {
                await deleteVehiclePlate(plate.id);
                toast.success('Placa deletada com sucesso', ToasterConfig.success as ToastOptions);
                loadVehiclePlates();
            }
        } catch (error: any) {
            setError(error.message);
        }
    }

    return (
        <div>
            <Header as="h3" textAlign="center" color={'green'}>
                Placas
            </Header>
            <Table.Row>
                <Table.HeaderCell>
                    {sessionUser?.userData?.profile === 'Admin' &&
                        <Button color="green" onClick={() => navigate('/backoffice/vehicle-plates/create/')}>
                            <Icon name='plus'/> Novo
                        </Button>}
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color="green">
                        <Icon name={showFilters ? 'hide' : 'unhide'}/>
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>
            </Table.Row>

            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={2}>
                                <label htmlFor="plate">Placa</label>
                                <Input
                                    fluid
                                    id="plate"
                                    icon="search"
                                    type="text"
                                    placeholder="Placa"
                                    value={filter?.plate ?? ''}
                                    onChange={(event) => setFilter({...filter, plate: event.target.value})}
                                />
                            </Grid.Column>

                            <Grid.Column width={3}>
                                <label htmlFor="clientName">Cliente</label>
                                <Form.Select
                                    options={clientes}
                                    placeholder="Cliente..."
                                    value={filter?.clientId ?? "-"}
                                    required
                                    search
                                    clearable
                                    onChange={(e: any, data: any) => {
                                        const selectedValue = data.value;
                                        setFilter(prevPlate => ({ ...prevPlate, clientId: selectedValue }));
                                    }}
                                />
                            </Grid.Column>

                            <Grid.Column width={2}>
                                <label htmlFor="tag">Tag</label>
                                <Input
                                    fluid
                                    id="tag"
                                    icon="search"
                                    type="text"
                                    placeholder="Tag"
                                    value={filter?.tag ?? ''}
                                    onChange={(event) => setFilter({...filter, tag: event.target.value})}
                                />
                            </Grid.Column>
                            <Grid.Column width={3}>
                                <label htmlFor="typeId">Tipo de Veículo</label>
                                <Form.Select
                                    options={vehicleTypes}
                                    placeholder="Tipo de veículo..."
                                    value={filter?.typeId ?? "-"}
                                    required
                                    search
                                    clearable
                                    onChange={(e: any, data: any) => {
                                        const selectedValue = data.value;
                                        setFilter(prevPlate => ({ ...prevPlate, typeId: selectedValue }));
                                    }}
                                />
                            </Grid.Column>

                            <Grid.Column width={3}>
                                <label htmlFor="fleetTypeId">Tipo de Frota</label>
                                <Form.Select
                                    options={fleetTypes}
                                    placeholder="Tipo de frota..."
                                    value={filter?.fleetTypeId ?? "-"}
                                    required
                                    search
                                    clearable
                                    onChange={(e: any, data: any) => {
                                        const selectedValue = data.value;
                                        setFilter(prevPlate => ({ ...prevPlate, fleetTypeId: selectedValue }));
                                    }}
                                />
                            </Grid.Column>
                            <Grid.Column width={2} verticalAlign="bottom">
                                <Button color="green" onClick={() => setFilter({...filter, Page: 1})}>Buscar</Button>
                            </Grid.Column>

                        </Grid.Row>
                    </Grid>
                </Segment>
            )}

            <Table color={"green"}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Placa</Table.HeaderCell>
                        <Table.HeaderCell>Tag</Table.HeaderCell>
                        <Table.HeaderCell>Tipo de Veículo</Table.HeaderCell>
                        <Table.HeaderCell>Tipo de Frota</Table.HeaderCell>
                        <Table.HeaderCell>Cliente</Table.HeaderCell>
                        <Table.HeaderCell>Criado em</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {vehiclePlates?.map((vehiclePlate) => (
                        <Table.Row key={vehiclePlate.id}>
                            <Table.Cell>
                                <Button.Group>
                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Editar placa de veículo"
                                            trigger={
                                                <Button
                                                    color="green"
                                                    onClick={() => navigate('/backoffice/vehicle-plates/edit/' + vehiclePlate.id)}
                                                    icon="edit"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Excluir placa de veículo"
                                            trigger={
                                                <Button
                                                    color="red"
                                                    onClick={() => deletePlateFunction(vehiclePlate)}
                                                    icon="trash"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content={vehiclePlate.isActive ? "Desativar placa de veículo" : "Ativar placa de veículo"}
                                            trigger={
                                                <Button
                                                    color={vehiclePlate.isActive ? 'red' : 'green'}
                                                    onClick={() => toggleActivationPlate(vehiclePlate)}
                                                    icon={vehiclePlate.isActive ? 'power off' : 'power'}
                                                />
                                            }
                                        />
                                    )}
                                </Button.Group>

                            </Table.Cell>
                            <Table.Cell>{vehiclePlate.plate}</Table.Cell>
                            <Table.Cell>{vehiclePlate.tag}</Table.Cell>
                            <Table.Cell>{vehiclePlate?.typeName}</Table.Cell>
                            <Table.Cell>{vehiclePlate?.fleetTypeName}</Table.Cell>
                            <Table.Cell>{vehiclePlate?.clientName}</Table.Cell>
                            <Table.Cell>{moment(vehiclePlate.createdAt).format('DD/MM/YY HH:mm')}</Table.Cell>
                            <Table.Cell>
                                <Label key={vehiclePlate.id} color={!vehiclePlate.isActive ? 'red' : 'green'}>
                                    {vehiclePlate.isActive ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>

            <Pagination
                activePage={filter?.Page || 1}
                onPageChange={(e, {activePage}) => setFilter({...filter, Page: activePage as number})}
                totalPages={totalPages || 1}
                siblingRange={1}
                boundaryRange={1}
                firstItem={null}
                lastItem={null}
                pointing
                secondary
            />
        </div>
    );
};

export default VehiclePlatesPage;
